// ============================================================
//   Tasas.js — RIFAS JORDYN
//   ✅ Tasa COP/USD  — configurable manualmente
//   ✅ Tasa BSD/USD  — bolívares digitales, configurable
//   ✅ Calculadora   — Bs → USD → COP con ambas tasas
//   ✅ Referencia    — dólar paralelo ve.dolarapi.com en vivo
//   ✅ Fechas        — America/Caracas (sin desfase UTC)
// ============================================================
import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import API from '../services/api';
import { toast } from 'react-toastify';

/* ─── Formateadores ─── */
const fmtCOP = v =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);

const fmtBs = v =>
  `Bs. ${new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0)}`;

const fmtFecha = f =>
  f ? new Date(f).toLocaleString('es-CO', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Caracas',
  }) : '—';

/* ─── Config visual de cada tasa ─── */
const TASAS_CONFIG = {
  COP_POR_USD: {
    label:       'Tasa COP / USD — solo Zelle',
    descripcion: 'Pesos colombianos por 1 dólar. Solo se usa para cobrar por Zelle. No afecta lo que se cobra en bolívares.',
    icono:       '🇨🇴',
    monedaBase:  'COP',
    monedaRef:   'USD',
    placeholder: '4200',
    step:        '1',
    hint:    v => `1 USD = ${fmtCOP(v)} · 1 COP = $${(1 / v).toFixed(6)} USD`,
    ejemplo: v => `Un boleto de ${fmtCOP(10000)} equivale a $${(10000 / v).toFixed(2)} USD`,
    accentColor: 'var(--jordyn-primary)',
    accentAlpha: 'rgba(10,191,188,',
  },
  COP_POR_USD_BS: {
    label:       'Tasa COP / USD — solo para bolívares',
    descripcion: 'Pesos colombianos por 1 dólar, usada únicamente para pasar el precio a bolívares (Pago Móvil y Banco de Venezuela). Es independiente de la de Zelle.',
    icono:       '🔁',
    monedaBase:  'COP',
    monedaRef:   'USD',
    placeholder: '3000',
    step:        '1',
    hint:    v => `Regla de tres: pesos ÷ ${new Intl.NumberFormat('es-CO').format(v)} × Bs por dólar`,
    ejemplo: v => `Si sube, se cobran menos bolívares; si baja, más. Un boleto de ${fmtCOP(60000)} = $${(60000 / v).toFixed(2)} × la tasa BsD/USD`,
    accentColor: '#7c3aed',
    accentAlpha: 'rgba(124,58,237,',
  },
  BSD_POR_USD: {
    label:       'Tasa BsD / USD',
    descripcion: 'Bolívares por 1 dólar (paralelo o BCV). Solo se usa para los pagos en bolívares.',
    icono:       '🇻🇪',
    monedaBase:  'BsD',
    monedaRef:   'USD',
    placeholder: '45.00',
    step:        '0.01',
    hint:    v => `1 USD = ${fmtBs(v)} · 1 BsD = $${(1 / v).toFixed(6)} USD`,
    ejemplo: v => `Por cada dólar del precio se cobran ${fmtBs(v)}`,
    accentColor: 'var(--jordyn-gold)',
    accentAlpha: 'rgba(240,165,0,',
  },
};

/* ─── Tasas del día que se pueden usar en cada tasa del sistema ─── */
const FUENTES_UI = {
  BSD_POR_USD: [
    { id: 'bcv',      nombre: 'BCV (oficial)',  sub: 'Banco Central de Venezuela',  icono: '🏛️', fmt: v => fmtBs(v) },
    { id: 'paralelo', nombre: 'Dólar paralelo', sub: 'Promedio del mercado',        icono: '📈', fmt: v => fmtBs(v) },
  ],
  COP_POR_USD: [
    { id: 'oficial',  nombre: 'Dólar oficial en Colombia', sub: 'Promedio compra / venta', icono: '🇨🇴', fmt: v => fmtCOP(v) },
  ],
  COP_POR_USD_BS: [
    { id: 'oficial',  nombre: 'Dólar oficial en Colombia', sub: 'Promedio compra / venta', icono: '🇨🇴', fmt: v => fmtCOP(v) },
  ],
};

/* ─── Hook: dólar paralelo Venezuela ─── */
function useDolarParalelo() {
  const [data, setData] = useState({ tasa: null, fuente: null, fecha: null });
  useEffect(() => {
    fetch('https://ve.dolarapi.com/v1/dolares')
      .then(r => r.json())
      .then(arr => {
        const paralelo = arr.find(d => d.fuente === 'paralelo');
        if (paralelo?.promedio) setData({
          tasa:  paralelo.promedio,
          fuente:'paralelo',
          fecha: paralelo.fechaActualizacion || null,
        });
      })
      .catch(() => {});
  }, []);
  return data;
}

/* ═══════════════════════════════════════════════════════════
   CALCULADORA Bs → USD → COP
═══════════════════════════════════════════════════════════ */
function Calculadora({ tasas }) {
  const [montoBS,  setMontoBS]  = useState('');
  const [resultado, setRes]     = useState(null);
  const paralelo = useDolarParalelo();

  // Bolívares ↔ pesos: con la tasa de pesos propia de los bolívares (no la de Zelle)
  const copUsd   = parseFloat(tasas?.COP_POR_USD_BS?.valor || tasas?.COP_POR_USD?.valor || 4200);
  const bsdUsd   = parseFloat(tasas?.BSD_POR_USD?.valor || 0);
  const tasaActiva = bsdUsd > 0 ? bsdUsd : (paralelo.tasa || 0);

  const calcular = () => {
    const bs = parseFloat(montoBS);
    if (!bs || bs <= 0)   { toast.error('Ingresa un monto en bolívares válido'); return; }
    if (!tasaActiva)       { toast.error('No hay tasa BsD/USD disponible. Guárdala primero.'); return; }
    const usd = bs / tasaActiva;
    const cop = usd * copUsd;
    setRes({ bs, tasaUsada: tasaActiva, usd, cop, copUsd,
      fuenteTasa: bsdUsd > 0 ? 'manual' : 'paralelo' });
  };

  return (
    <div className="jd-card fade-in" style={{ borderLeft: '3px solid var(--jordyn-gold)' }}>

      {/* Título */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:18 }}>
        <div style={{
          width:40, height:40, borderRadius:10, flexShrink:0,
          background:'rgba(240,165,0,.12)', border:'2px solid rgba(240,165,0,.3)',
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem',
        }}>💱</div>
        <div>
          <div style={{ fontWeight:800, fontSize:'.92rem', color:'var(--jordyn-gold)' }}>
            Calculadora Bs → COP
          </div>
          <div style={{ fontSize:'.65rem', color:'var(--jordyn-muted)', marginTop:1 }}>
            Convierte bolívares a pesos colombianos usando las tasas configuradas arriba
          </div>
        </div>
      </div>

      <div className="row g-3 align-items-end">

        {/* Input monto */}
        <div className="col-12 col-md-5">
          <label className="jd-label">MONTO EN BOLÍVARES (BsD)</label>
          <div style={{ position:'relative' }}>
            <input
              className="jd-input"
              type="number"
              min="0"
              step="0.01"
              placeholder="Ej: 500.00"
              value={montoBS}
              onChange={e => { setMontoBS(e.target.value); setRes(null); }}
              style={{ fontWeight:800, fontSize:'1.1rem', paddingRight:52 }}
            />
            <span style={{
              position:'absolute', right:14, top:'50%', transform:'translateY(-50%)',
              fontSize:'.7rem', fontWeight:700, color:'var(--jordyn-muted)',
            }}>BsD</span>
          </div>
          {/* Indicador de tasa a usar */}
          <div style={{ fontSize:'.64rem', color:'var(--jordyn-muted)', marginTop:5, lineHeight:1.5 }}>
            {bsdUsd > 0
              ? <><i className="bi bi-check-circle-fill me-1" style={{ color:'var(--jordyn-primary)' }}></i>
                  Tasa manual: 1 USD = {fmtBs(bsdUsd)}</>
              : paralelo.tasa
                ? <><i className="bi bi-wifi me-1" style={{ color:'var(--jordyn-gold)' }}></i>
                    Sin tasa manual — usando paralelo: 1 USD = {fmtBs(paralelo.tasa)}</>
                : <><i className="bi bi-exclamation-circle me-1" style={{ color:'#e63946' }}></i>
                    Sin tasa BsD configurada ni API disponible</>
            }
          </div>
        </div>

        {/* Tasas activas (referencia visual) */}
        <div className="col-12 col-md-4">
          <div style={{
            background:'var(--jordyn-bg2)', border:'1px solid var(--jordyn-border)',
            borderRadius:10, padding:'10px 14px',
          }}>
            <div style={{ fontSize:'.55rem', fontWeight:700, color:'var(--jordyn-muted)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:6 }}>
              Tasas activas
            </div>
            <div style={{ fontSize:'.78rem', fontWeight:700, lineHeight:1.9 }}>
              <span style={{ color:'var(--jordyn-gold)' }}>
                1 USD = {tasaActiva ? fmtBs(tasaActiva) : '—'}
              </span>
              <br />
              <span style={{ color:'var(--jordyn-primary)' }}>
                1 USD = {fmtCOP(copUsd)}
              </span>
            </div>
          </div>
        </div>

        {/* Botón */}
        <div className="col-12 col-md-3">
          <button
            className="btn-jordyn w-100"
            onClick={calcular}
            style={{ fontSize:'.88rem' }}
          >
            <i className="bi bi-calculator-fill me-1"></i> Convertir
          </button>
        </div>
      </div>

      {/* Resultado */}
      {resultado && (
        <div className="fade-in" style={{
          marginTop:18, padding:'16px 20px',
          background:'rgba(240,165,0,.06)', border:'1.5px solid rgba(240,165,0,.25)',
          borderRadius:12,
        }}>
          <div style={{ fontSize:'.56rem', fontWeight:700, color:'var(--jordyn-muted)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:12 }}>
            Resultado de conversión
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:16, alignItems:'center' }}>

            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:'.5rem', color:'var(--jordyn-muted)', marginBottom:2 }}>Bolívares</div>
              <div style={{ fontSize:'1.1rem', fontWeight:900, color:'var(--jordyn-gold)' }}>
                {fmtBs(resultado.bs)}
              </div>
            </div>

            <div style={{ color:'var(--jordyn-muted)', fontSize:'1.2rem', fontWeight:700 }}>→</div>

            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:'.5rem', color:'var(--jordyn-muted)', marginBottom:2 }}>Pesos colombianos</div>
              <div style={{ fontSize:'1.35rem', fontWeight:900, color:'var(--jordyn-primary)' }}>
                {fmtCOP(resultado.cop)}
              </div>
            </div>

            <div style={{ marginLeft:'auto', fontSize:'.62rem', color:'var(--jordyn-muted)', textAlign:'right', lineHeight:1.7, flexShrink:0 }}>
              <i className={`bi bi-${resultado.fuenteTasa === 'manual' ? 'sliders' : 'wifi'} me-1`}></i>
              Bs/USD: {fmtBs(resultado.tasaUsada)} ({resultado.fuenteTasa === 'manual' ? 'manual' : 'paralelo'})
              <br />
              COP/USD: {fmtCOP(resultado.copUsd)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════════════════ */
export default function Tasas() {
  const [tasas,   setTasas]   = useState({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState('');
  const [edits,   setEdits]   = useState({});
  const [refs,    setRefs]    = useState(null);   // { referencias, fuentes }
  const [aplicando, setAplicando] = useState('');

  const loadRefs = useCallback(async (fresca = false) => {
    try { const r = await API.get(`/tasas/referencias${fresca ? '?fresca=1' : ''}`); setRefs(r.data); }
    catch { setRefs(p => p || { referencias: {}, fuentes: {} }); }
  }, []);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  const load = useCallback(async () => {
    try {
      const res = await API.get('/tasas');
      setTasas(res.data);
      const init = {};
      Object.entries(res.data).forEach(([k, v]) => { init[k] = String(v.valor); });
      setEdits(init);
    } catch { toast.error('Error cargando tasas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (clave) => {
    const valorStr = edits[clave];
    const valor    = parseFloat(valorStr?.replace(/[^0-9.]/g, ''));
    if (!valor || valor <= 0) { toast.error('Ingresa un valor válido mayor a cero'); return; }
    setSaving(clave);
    try {
      await API.put(`/tasas/${clave}`, { valor });
      toast.success('✅ Tasa actualizada correctamente');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar');
    } finally { setSaving(''); }
  };

  // Copiar una vez el valor del día (la tasa sigue manual)
  const usarHoy = async (clave, f) => {
    setAplicando(`${clave}:${f.id}`);
    try {
      const r = await API.post(`/tasas/${clave}/sincronizar`, { fuente: f.id });
      toast.success(`✅ Tasa actualizada con ${f.nombre}: ${f.fmt(r.data.valor)}`);
      await load(); loadRefs();
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo consultar la tasa'); }
    finally { setAplicando(''); }
  };
  // Dejarla siguiendo sola esa fuente (o volver a manual)
  const seguir = async (clave, fuente, nombre) => {
    setAplicando(`${clave}:auto`);
    try {
      await API.put(`/tasas/${clave}/fuente`, { fuente });
      toast.success(fuente === 'manual' ? 'La tasa quedó en manual' : `✅ Desde ahora sigue sola a ${nombre}`);
      await load(); loadRefs();
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo cambiar'); }
    finally { setAplicando(''); }
  };

  const cfg = clave => TASAS_CONFIG[clave] || {
    label: clave, descripcion: '', icono: '💱',
    monedaBase: '', monedaRef: '', placeholder: '0', step: '1',
    hint: () => '', ejemplo: () => '',
    accentColor: 'var(--jordyn-primary)',
    accentAlpha: 'rgba(10,191,188,',
  };

  // Orden fijo: COP primero, BSD segundo
  const ORDER  = ['COP_POR_USD', 'BSD_POR_USD', 'COP_POR_USD_BS'];
  const allKeys = { ...TASAS_CONFIG, ...tasas };
  const claves = [
    ...ORDER.filter(k => k in allKeys),
    ...Object.keys(allKeys).filter(k => !ORDER.includes(k)),
  ];

  return (
    <Layout title="TASAS DE CAMBIO">

      {/* Descripción */}
      <div style={{ marginBottom:28 }}>
        <p style={{ fontSize:'.82rem', color:'var(--jordyn-muted)', margin:0, lineHeight:1.6 }}>
          <i className="bi bi-info-circle me-1"></i>
          Configura aquí las tasas del sistema.
          <strong> COP/USD</strong> es solo para Zelle.
          Los bolívares (Pago Móvil) se calculan con su regla de tres aparte: precio en pesos ÷ <strong>COP/USD para bolívares</strong> × <strong>BsD/USD</strong>.
          Cambiar la de Zelle no mueve lo que se cobra en bolívares.
          Puedes escribirlas a mano o tomarlas del <strong>BCV</strong>, del paralelo o del dólar oficial de Colombia:
          una sola vez, o dejarlas en automático para que se actualicen solas.
          El cliente nunca ve la tasa, solo el monto convertido.
        </p>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center mt-5">
          <div className="jd-spinner" style={{ width:40, height:40 }}></div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

          {/* ─── Card por cada tasa ─── */}
          {claves.map(clave => {
            const c     = cfg(clave);
            const tasa  = tasas[clave];
            const valor = parseFloat(edits[clave] || tasa?.valor || 0);
            const dirty = parseFloat(edits[clave]) !== tasa?.valor;
            const aa    = c.accentAlpha || 'rgba(10,191,188,';

            return (
              <div key={clave} className="jd-card jd-card-primary fade-in">

                {/* Cabecera */}
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{
                      width:48, height:48, borderRadius:12, flexShrink:0,
                      background:`${aa}0.10)`, border:`2px solid ${aa}0.25)`,
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.5rem',
                    }}>{c.icono}</div>
                    <div>
                      <div style={{ fontWeight:800, fontSize:'1rem', color:c.accentColor }}>{c.label}</div>
                      <div style={{ fontSize:'.72rem', color:'var(--jordyn-muted)', marginTop:2 }}>{c.descripcion}</div>
                    </div>
                  </div>
                  {tasa?.updated_at && (
                    <div style={{ fontSize:'.65rem', color:'var(--jordyn-muted)', textAlign:'right', flexShrink:0 }}>
                      <i className="bi bi-clock me-1"></i>
                      Actualizado: {fmtFecha(tasa.updated_at)}
                    </div>
                  )}
                </div>

                <div className="row g-3 align-items-end">

                  {/* Input */}
                  <div className="col-12 col-md-5">
                    <label className="jd-label">1 {c.monedaRef} = ? {c.monedaBase}</label>
                    <div style={{ position:'relative' }}>
                      <input
                        className="jd-input"
                        type="number"
                        min="0.01"
                        step={c.step}
                        value={edits[clave] ?? ''}
                        onChange={e => setEdits(p => ({ ...p, [clave]: e.target.value }))}
                        placeholder={c.placeholder}
                        style={{ fontWeight:800, fontSize:'1.1rem', paddingRight:56 }}
                      />
                      <span style={{
                        position:'absolute', right:14, top:'50%', transform:'translateY(-50%)',
                        fontSize:'.72rem', fontWeight:700, color:'var(--jordyn-muted)',
                      }}>{c.monedaBase}</span>
                    </div>
                    {valor > 0 && (
                      <div style={{ fontSize:'.7rem', color:c.accentColor, marginTop:5, fontWeight:600 }}>
                        {c.hint(valor)}
                      </div>
                    )}
                  </div>

                  {/* Ejemplo */}
                  <div className="col-12 col-md-4">
                    {valor > 0 && (
                      <div style={{
                        background:'var(--jordyn-bg2)', border:'1px solid var(--jordyn-border)',
                        borderRadius:10, padding:'10px 14px',
                      }}>
                        <div style={{ fontSize:'.58rem', fontWeight:700, color:'var(--jordyn-muted)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:4 }}>
                          Ejemplo
                        </div>
                        <div style={{ fontSize:'.78rem', color:'var(--jordyn-text)', fontWeight:600 }}>
                          {c.ejemplo(valor)}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Botón guardar */}
                  <div className="col-12 col-md-3">
                    <button
                      className="btn-jordyn w-100"
                      onClick={() => handleSave(clave)}
                      disabled={saving === clave || !dirty}
                      style={{ fontSize:'.85rem' }}
                    >
                      {saving === clave
                        ? <><span className="jd-spinner" style={{ width:14, height:14, borderWidth:2 }}></span> Guardando...</>
                        : dirty
                          ? <><i className="bi bi-floppy-fill me-1"></i>Guardar</>
                          : <><i className="bi bi-check2 me-1"></i>Guardado</>
                      }
                    </button>
                  </div>
                </div>

                {/* ─── Tasas del día: BCV / paralelo / oficial de Colombia ─── */}
                {FUENTES_UI[clave] && (() => {
                  const fuenteActual = refs?.fuentes?.[clave]?.fuente || 'manual';
                  const info = refs?.fuentes?.[clave];
                  return (
                    <div style={{ marginTop:18, borderTop:'1px solid var(--jordyn-border)', paddingTop:16, display:'flex', flexDirection:'column', gap:10 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                        <div style={{ fontSize:'.66rem', fontWeight:700, color:'var(--jordyn-muted)', textTransform:'uppercase', letterSpacing:'1px' }}>
                          <i className="bi bi-globe2 me-1"></i>Tasas del día
                        </div>
                        <span style={{ fontSize:'.68rem', fontWeight:800, borderRadius:20, padding:'2px 10px',
                          background: fuenteActual === 'manual' ? 'var(--jordyn-bg2)' : 'rgba(6,214,160,.12)', color: fuenteActual === 'manual' ? 'var(--jordyn-muted)' : '#059669' }}>
                          {fuenteActual === 'manual' ? 'Manual' : `Automática · ${FUENTES_UI[clave].find(f => f.id === fuenteActual)?.nombre || fuenteActual}`}
                        </span>
                      </div>

                      {FUENTES_UI[clave].map(f => {
                        const ref = refs?.referencias?.[f.id];
                        const siguiendo = fuenteActual === f.id;
                        return (
                          <div key={f.id} style={{ background:`${aa}0.06)`, border:`1.5px solid ${siguiendo ? 'rgba(6,214,160,.6)' : `${aa}0.20)`}`, borderRadius:10, padding:'12px 16px',
                            display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:170 }}>
                              <span style={{ fontSize:'1.3rem' }}>{f.icono}</span>
                              <div>
                                <div style={{ fontSize:'.78rem', fontWeight:700, color:'var(--jordyn-text)' }}>{f.nombre}</div>
                                <div style={{ fontSize:'.6rem', color:'var(--jordyn-muted)' }}>{f.sub}{ref?.fecha ? ` · ${fmtFecha(ref.fecha)}` : ''}</div>
                              </div>
                            </div>
                            <div style={{ fontSize:'1.1rem', fontWeight:900, color:c.accentColor }}>
                              {!refs ? <span className="jd-spinner" style={{ width:14, height:14, borderWidth:2, display:'inline-block' }}></span>
                                : ref?.valor ? <>{f.fmt(ref.valor)} <span style={{ fontSize:'.58rem', color:'var(--jordyn-muted)', fontWeight:600 }}>por 1 USD</span></>
                                : <span style={{ fontSize:'.72rem', color:'var(--jordyn-muted)', fontWeight:600 }}>No disponible ahora</span>}
                            </div>
                            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                              <button type="button" className="btn-jordyn-outline" disabled={!ref?.valor || !!aplicando} onClick={() => usarHoy(clave, f)}
                                title="Copia este valor a la tasa una sola vez" style={{ fontSize:'.74rem', padding:'5px 11px' }}>
                                {aplicando === `${clave}:${f.id}` ? 'Aplicando…' : <><i className="bi bi-download me-1"></i>Usar este valor</>}
                              </button>
                              <button type="button" disabled={!!aplicando || (!siguiendo && !ref?.valor)} onClick={() => seguir(clave, siguiendo ? 'manual' : f.id, f.nombre)}
                                title={siguiendo ? 'Dejar de actualizarla sola' : 'La tasa se actualiza sola cada hora con este valor'}
                                style={{ fontSize:'.74rem', padding:'5px 11px', borderRadius:8, cursor:'pointer', fontWeight:700,
                                  border:`1.5px solid ${siguiendo ? '#059669' : 'var(--jordyn-border)'}`, background: siguiendo ? '#059669' : '#fff', color: siguiendo ? '#fff' : 'var(--jordyn-text)' }}>
                                <i className={`bi ${siguiendo ? 'bi-check2-circle' : 'bi-arrow-repeat'} me-1`}></i>{siguiendo ? 'En automático' : 'Automática'}
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      <div style={{ background:'rgba(240,165,0,.05)', border:'1px solid rgba(240,165,0,.18)', borderRadius:8, padding:'8px 12px', fontSize:'.7rem', color:'var(--jordyn-muted)', lineHeight:1.55 }}>
                        <i className="bi bi-lightbulb me-1" style={{ color:'var(--jordyn-gold)' }}></i>
                        {fuenteActual === 'manual'
                          ? <>Está en <strong>manual</strong>: vale lo que escribas arriba. «Usar este valor» lo copia una vez; «Automática» la deja actualizándose sola cada hora.</>
                          : <>Se actualiza sola cada hora{info?.revisado_at ? <> (última revisión: {fmtFecha(info.revisado_at)})</> : null}. Si escribes un valor a mano y guardas, vuelve a manual.</>}
                        {info?.error && <span style={{ color:'#c62e3a', fontWeight:700 }}> No se pudo consultar la última vez: se mantiene el valor anterior.</span>}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })}

          {/* ─── Calculadora Bs → COP ─── */}
          <Calculadora tasas={tasas} />

          {/* Sin tasas en BD */}
          {Object.keys(tasas).length === 0 && (
            <div className="jd-alert jd-alert-warning">
              <i className="bi bi-exclamation-triangle-fill"></i>
              No hay tasas configuradas. Verifica que la migración SQL se ejecutó correctamente.
            </div>
          )}

        </div>
      )}
    </Layout>
  );
}